import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';

export default function FAQ() {
  const faqs = [
    {
      category: "💳 Billing & Payments",
      questions: [
        {
          q: "How does billing work?",
          a: "You can choose monthly or annual billing. Monthly plans renew automatically every month, while annual plans give you 2 months free and renew yearly."
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept all major credit/debit cards (Visa, Mastercard, JCB, American Express) through PayMongo, our secure payment processor."
        },
        {
          q: "Can I cancel anytime?",
          a: "Yes! You can downgrade to the Free plan or cancel your subscription at any time from your account settings. No cancellation fees."
        },
        {
          q: "Do you offer refunds?",
          a: "Yes! We offer a 30-day money-back guarantee. If you're not satisfied for any reason within 30 days of purchase, contact us at support@sitescope.work for a full refund."
        }
      ]
    },
    {
      category: "🚀 Product & Features",
      questions: [
        {
          q: "How often does SiteScope check my website?",
          a: "Free Plan: Every 15 minutes | Pro Plan: Every 3 minutes | Enterprise Plan: Every 1 minute"
        },
        {
          q: "What happens when my site goes down?",
          a: "You'll receive instant alerts via email and push notifications (if enabled). We'll continue monitoring and alert you when it comes back up."
        },
        {
          q: "How many websites can I monitor?",
          a: "Free: Up to 3 websites | Pro: Up to 15 websites | Enterprise: Up to 200 websites"
        },
        {
          q: "Do you monitor SSL certificates?",
          a: "Yes! We automatically check SSL certificate expiration and alert you 30 days before expiry (configurable to 7, 14, 30, 60, or 90 days)."
        }
      ]
    },
    {
      category: "🔒 Security & Privacy",
      questions: [
        {
          q: "Is my data secure?",
          a: "Yes. We use industry-standard encryption (SSL/TLS), secure database storage, and never share your data with third parties."
        },
        {
          q: "Where is my data stored?",
          a: "Your data is securely stored on Railway's cloud infrastructure with automatic daily backups."
        },
        {
          q: "Do you sell my data?",
          a: "Never. We respect your privacy and only use your data to provide monitoring services."
        }
      ]
    },
    {
      category: "🛠️ Technical",
      questions: [
        {
          q: "What if I need help?",
          a: "Email us at support@sitescope.work and we'll respond within 24-48 hours (usually faster!)."
        },
        {
          q: "Can I upgrade or downgrade my plan?",
          a: "Yes! You can change your plan anytime from your account settings. Upgrades take effect immediately. Downgrades take effect at the end of your current billing period."
        },
        {
          q: "What happens if I exceed my site limit?",
          a: "You'll need to upgrade to a higher plan or remove sites to add new ones. We'll notify you when you're approaching your limit."
        },
        {
          q: "Do you have an API?",
          a: "Not yet, but it's on our roadmap! Let us know at support@sitescope.work if you'd like to be notified when it's available."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex flex-col">
      {/* Header */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6 w-full">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
            <img 
              src="/logo-icon.png" 
              alt="SiteScope" 
              className="w-12 h-12 sm:w-12 sm:h-12 md:w-14 md:h-14 transition-transform group-hover:scale-105" 
            />
            <span className="text-2xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
              SiteScope
            </span>
          </Link>
          
          <Link 
            to="/" 
            className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            ← Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16 w-full">
        <div className="text-center mb-12 sm:mb-16">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4 sm:mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-base sm:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Everything you need to know about SiteScope
          </p>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-12">
          {faqs.map((category, idx) => (
            <div key={idx} className="bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                {category.category}
              </h2>
              
              <div className="space-y-6">
                {category.questions.map((item, qIdx) => (
                  <div key={qIdx} className="border-b border-gray-200 dark:border-gray-700 last:border-0 pb-6 last:pb-0">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {item.q}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 whitespace-pre-line">
                      {item.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="mt-12 bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-8 border border-blue-200 dark:border-blue-800 text-center">
          <h3 className="text-2xl font-bold text-blue-900 dark:text-blue-100 mb-4">
            Still have questions?
          </h3>
          <p className="text-blue-700 dark:text-blue-300 mb-6">
            We're here to help! Reach out to our support team.
          </p>
          <a 
            href="mailto:support@sitescope.work"
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            Email Support
          </a>
        </div>
      </main>

      <Footer />
    </div>
  );
}
